import { useEffect } from "react";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { Seo } from "./Seo";
import { SITE_URL, LEGAL_SEO } from "@/data/prestations";

const Block = ({ title, children, testId }) => (
  <section className="mb-12" data-testid={testId}>
    <h2 className="font-heading text-xl font-semibold tracking-tight text-nuit">{title}</h2>
    <div className="mt-4 space-y-3 text-sm leading-relaxed text-anthracite/75">{children}</div>
  </section>
);

const LegalShell = ({ chapter, title, children, testId, seoTitle, seoDesc, seoPath }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-casse font-body text-anthracite" data-testid={testId}>
      <Seo title={seoTitle} description={seoDesc} canonical={`${SITE_URL}${seoPath}`} />
      <Header />
      <main className="mx-auto max-w-3xl px-6 pt-40 pb-24 lg:pb-32">
        <div className="flex items-center gap-4 mb-8">
          <span className="h-px w-12 bg-sable" aria-hidden="true" />
          <span className="font-accent text-xs font-medium tracking-[0.32em] uppercase text-anthracite/50">{chapter}</span>
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-nuit">{title}</h1>
        <div className="mt-12">{children}</div>
      </main>
      <Footer />
    </div>
  );
};

export const MentionsLegales = () => (
  <LegalShell
    chapter="Informations légales"
    title="Mentions légales"
    testId="mentions-legales-page"
    seoTitle={LEGAL_SEO["mentions-legales"].title}
    seoDesc={LEGAL_SEO["mentions-legales"].description}
    seoPath={LEGAL_SEO["mentions-legales"].path}
  >
    <Block title="Éditeur du site" testId="legal-editeur">
      <p>
        Le site est édité par <strong className="text-nuit">LIMEN Aménagement &amp; Rénovation</strong>,
        entreprise spécialisée en isolation intérieure, plaquisterie et aménagement intérieur, intervenant
        en Seine-et-Marne (77) et dans les secteurs limitrophes.
      </p>
      <p>Téléphone : 07 86 25 69 65 — E-mail : contact@limen-ar.fr</p>
      <p className="text-anthracite/55 italic">
        Forme juridique, capital, SIRET et adresse du siège : en cours d'immatriculation — informations
        ajoutées dès leur publication officielle.
      </p>
    </Block>
    <Block title="Directeur de la publication" testId="legal-publication">
      <p>Le directeur de la publication est le représentant légal de LIMEN Aménagement &amp; Rénovation.</p>
    </Block>
    <Block title="Hébergement" testId="legal-hebergement">
      <p>
        Ce site est hébergé sur l'infrastructure Emergent (emergent.sh). Les informations détaillées de
        l'hébergeur définitif seront précisées lors de la mise en production sur
        limen-ar.fr.
      </p>
    </Block>
    <Block title="Propriété intellectuelle" testId="legal-propriete">
      <p>
        L'ensemble des contenus du site (textes, visuels, identité graphique, logotype LIMEN) est la
        propriété exclusive de LIMEN Aménagement &amp; Rénovation. Toute reproduction, représentation ou
        diffusion, totale ou partielle, sans autorisation écrite préalable est interdite.
      </p>
    </Block>
    <Block title="Crédits" testId="legal-credits">
      <p>Conception et développement du site : LIMEN Aménagement &amp; Rénovation.</p>
    </Block>
  </LegalShell>
);

export const PolitiqueConfidentialite = () => (
  <LegalShell
    chapter="Protection des données"
    title="Politique de confidentialité"
    testId="politique-confidentialite-page"
    seoTitle={LEGAL_SEO["politique-de-confidentialite"].title}
    seoDesc={LEGAL_SEO["politique-de-confidentialite"].description}
    seoPath={LEGAL_SEO["politique-de-confidentialite"].path}
  >
    <Block title="Données collectées" testId="privacy-donnees">
      <p>
        Dans le cadre du formulaire « Demander une étude », nous collectons uniquement les informations
        que vous renseignez : nom et prénom, adresse e-mail, numéro de téléphone, ville du chantier,
        type de projet, surface approximative, date souhaitée, message, ainsi que les plans ou photos
        que vous joignez volontairement.
      </p>
    </Block>
    <Block title="Finalité du traitement" testId="privacy-finalite">
      <p>
        Ces données sont utilisées exclusivement pour traiter votre demande : organiser un premier
        échange, préparer une visite technique et établir un devis. Elles ne font l'objet d'aucune
        prospection commerciale et ne sont ni cédées ni vendues à des tiers.
      </p>
    </Block>
    <Block title="Base légale et durée de conservation" testId="privacy-conservation">
      <p>
        Le traitement repose sur votre consentement et sur l'exécution de mesures précontractuelles.
        Vos données sont conservées pendant la durée nécessaire au traitement de votre demande, puis
        archivées ou supprimées au plus tard 3 ans après notre dernier échange.
      </p>
    </Block>
    <Block title="Vos droits" testId="privacy-droits">
      <p>
        Conformément au RGPD et à la loi Informatique et Libertés, vous disposez d'un droit d'accès,
        de rectification, d'effacement, de limitation et d'opposition concernant vos données. Pour
        exercer ces droits : contact@limen-ar.fr ou 07 86 25 69 65.
      </p>
      <p>
        Vous pouvez également introduire une réclamation auprès de la CNIL (cnil.fr) si vous estimez
        que vos droits ne sont pas respectés.
      </p>
    </Block>
    <Block title="Cookies et mesure d'audience" testId="privacy-cookies">
      <p>
        Ce site n'utilise aucun cookie publicitaire ni traceur tiers. Aucune donnée de navigation
        n'est exploitée à des fins commerciales.
      </p>
    </Block>
  </LegalShell>
);
